import dayjs from 'dayjs';
import { usesCommaDecimalSeparator } from '../constants/utilityFunctions';
import { IMatter, ITransaction } from '../interface';
import { IPrincipalCompany } from '../interface/principalCompany';
import { IPrincipalPrivatePerson } from '../interface/principalPrivatePerson';
import {
  IDailyGroupedTransactions,
  IHourTransaction,
  ILast7DaysHourTransactionDashboard,
} from '../interface/transaction';
import { groupTransactionsByDay } from '../constants/transactionUtility';

interface ILastWeekDay {
  number: string;
  weekDay: string;
  date: string;
}

export const getLastWeekDays = (nineDaysAgoDate: string): ILastWeekDay[] => {
  const allDays: ILastWeekDay[] = [];
  for (let i = 0; i <= 10; i++) {
    const weekDay = dayjs(nineDaysAgoDate).add(i, 'day');
    allDays.push({
      number: weekDay.format('D'),
      weekDay: weekDay.format('ddd'),
      date: weekDay.utc(true).toISOString(),
    });
  }

  return allDays;
};

export const getVisibleWeekDays = (
  nineDaysAgoDate: string,
  data: ITransaction[],
): ILastWeekDay[] => {
  const allDays = getLastWeekDays(nineDaysAgoDate);

  const validDays: ILastWeekDay[] = [];

  // Go through all days and collect valid ones
  allDays.forEach((day) => {
    const dayMoment = dayjs(day.date);
    const isWeekend = dayMoment.day() === 0 || dayMoment.day() === 6;

    if (isWeekend) {
      const hasTransactions = data.some((transaction) => {
        const transactionDate = dayjs(transaction.transactionDate).format('YYYY-MM-DD');
        const dayDate = dayMoment.format('YYYY-MM-DD');
        return transactionDate === dayDate;
      });

      if (hasTransactions) {
        validDays.push(day);
      }
    } else {
      validDays.push(day);
    }
  });
  const visibleDays = validDays.slice(-7);

  return visibleDays;
};
export interface ISummarizeBillableHours {
  summary: string;
  matterIds: string[];
  date: string;
}
export function filterLast7DaysWithWeekends(
  paginatedTransactionData: ITransaction[],
  last7DaysTransactionsChartData?: ILast7DaysHourTransactionDashboard[],
): IDailyGroupedTransactions[] {
  const last10Days = Array.from({ length: 11 }, (_, i) =>
    dayjs().subtract(i, 'days').format('YYYY-MM-DD'),
  );

  const filteredData = paginatedTransactionData.filter((transaction) => {
    const transactionDate = transaction.transactionDate;
    return last10Days.includes(transactionDate);
  });

  const daysWithTransactions = new Set(
    filteredData.map((transaction) => transaction.transactionDate),
  );

  const last7DaysWithTransactions = last10Days.filter((day) => {
    const isWeekend =
      dayjs(day, 'YYYY-MM-DD').isoWeekday() === 6 || dayjs(day, 'YYYY-MM-DD').isoWeekday() === 7;
    return daysWithTransactions.has(day) || !isWeekend;
  });

  const finalDays = new Set(last7DaysWithTransactions.slice(0, 7));

  const finalFilteredData = filteredData.filter((transaction) => {
    return finalDays.has(transaction.transactionDate);
  });

  return groupTransactionsByDay(finalFilteredData, last7DaysTransactionsChartData);
}

export const getCurrentWeekDays = (now: dayjs.Dayjs = dayjs()): string[] => {
  const monday = now.startOf('isoWeek');
  return Array.from({ length: 7 }, (_, offset) => monday.add(offset, 'day').format('YYYY-MM-DD'));
};

export const fillCurrentWeekHours = (hourTransactions: IHourTransaction[]): IHourTransaction[] => {
  const billableHoursByDate = new Map(
    hourTransactions.map(({ transactionDate, billableHours }) => [transactionDate, billableHours]),
  );
  return getCurrentWeekDays().map((transactionDate) => ({
    transactionDate,
    billableHours: billableHoursByDate.get(transactionDate) ?? 0,
  }));
};

export function filterCurrentWeek(paginatedTransactionData: ITransaction[]) {
  const weekDays = new Set(getCurrentWeekDays());
  return groupTransactionsByDay(
    paginatedTransactionData.filter((transaction) => weekDays.has(transaction.transactionDate)),
  );
}

export const summarizeBillableHours = (
  data: ITransaction[],
  nineDaysAgoDate: string,
): ISummarizeBillableHours[] => {
  const grouped = filterLast7DaysWithWeekends(data);
  const visibleDays = getVisibleWeekDays(nineDaysAgoDate, data);

  const sums: ISummarizeBillableHours[] = visibleDays.map((el) => {
    const group = grouped.find(
      (g) => dayjs(g.date).format('YYYY-MM-DD') === dayjs(el.date).format('YYYY-MM-DD'),
    );

    const amount =
      Math.floor(group?.amount ?? 0) === group?.amount
        ? group?.amount.toFixed(0)
        : group?.amount.toFixed(2);
    const formattedHoursWithSeparator = usesCommaDecimalSeparator()
      ? amount?.replace('.', ',')
      : amount;

    return {
      summary: formattedHoursWithSeparator ?? '0',
      matterIds: group?.transactions.map((t) => t.matterId) ?? [],
      date: dayjs(el.date).format('YYYY-MM-DD'),
    };
  });
  return sums;
};

interface IRowData {
  hours: string | null;
  date: string;
  totalTransactions: number | null;
}

export interface IWeeklyDataRow {
  matter: IMatter;
  principalCompany?: IPrincipalCompany;
  principalPrivatePerson?: IPrincipalPrivatePerson;
  rowData: IRowData[];
}

export const weeklyDataRow = (
  data: ITransaction[],
  nineDaysAgoDate: string,
  temporaryMatter?: IMatter,
  searchValue?: string,
) => {
  const final: IWeeklyDataRow[] = [];
  const unique = [...new Map(data.map((el) => [el.matterId, el])).values()];
  const visibleDays = getVisibleWeekDays(nineDaysAgoDate, data);

  unique.forEach((el) => {
    const all = data.filter((e) => e.matterId === el.matterId);
    const rowData: IRowData[] = [];

    visibleDays.forEach((day) => {
      const dayMoment = dayjs(day.date);

      let transactionExistsOnCurrentDate = false;

      const hours = all
        .filter(
          (e) => dayMoment.format('YYYY-MM-DD') === dayjs(e.transactionDate).format('YYYY-MM-DD'),
        )
        ?.reduce((a, b) => a + b.billableQuantity, 0);

      transactionExistsOnCurrentDate =
        all.some(
          (e) => dayMoment.format('YYYY-MM-DD') === dayjs(e.transactionDate).format('YYYY-MM-DD'),
        ) ?? false;

      const transactions = all.filter(
        (e) => dayMoment.format('YYYY-MM-DD') === dayjs(e.transactionDate).format('YYYY-MM-DD'),
      );

      const formattedHours = Math.floor(hours) === hours ? hours.toFixed(0) : hours.toFixed(2);
      const formattedHoursWithSeparator = usesCommaDecimalSeparator()
        ? formattedHours.replace('.', ',')
        : formattedHours;

      rowData.push(
        transactionExistsOnCurrentDate
          ? {
              hours: formattedHoursWithSeparator,
              date: dayMoment.format('YYYY-MM-DD'),
              totalTransactions: transactions.length,
            }
          : { hours: null, date: dayMoment.format('YYYY-MM-DD'), totalTransactions: null },
      );
    });

    final.push({
      matter: el.matter,
      rowData,
      principalCompany: el.principalCompany,
      principalPrivatePerson: el.principalPrivatePerson,
    });
  });

  if (temporaryMatter) {
    const matchesSearch =
      !searchValue ||
      temporaryMatter.subject?.toLowerCase().includes(searchValue.toLowerCase()) ||
      temporaryMatter.matterNumber?.toString().toLowerCase().includes(searchValue.toLowerCase()) ||
      temporaryMatter.principalCompany?.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
      temporaryMatter.principalPrivatePerson?.fullName
        ?.toLowerCase()
        .includes(searchValue.toLowerCase());

    if (matchesSearch) {
      const rowData: IRowData[] = [];
      visibleDays.forEach((day) => {
        const dayMoment = dayjs(day.date);
        rowData.push({
          hours: null,
          date: dayMoment.format('YYYY-MM-DD'),
          totalTransactions: null,
        });
      });

      final.push({
        matter: temporaryMatter,
        rowData,
        principalCompany: temporaryMatter?.principalCompany,
        principalPrivatePerson: temporaryMatter?.principalPrivatePerson,
      });
    }
  }
  return final;
};
